import React, { useState, useMemo, useEffect } from 'react';
import { useAuth } from './hooks/useAuth';
import { useUserData } from './hooks/useUserData';
import { useBlogPosts } from './hooks/useBlogPosts';
import { useAdmin } from './hooks/useAdmin';

// Layout Components
import NavBar from './components/layout/NavBar';

// Page Components
import LatestPostDisplay from './components/blog/LatestPostDisplay';
import BlogSidebar from './components/blog/BlogSidebar';
import EditPostForm from './components/blog/EditPostForm';
import AppsPage from './components/pages/AppsPage';
import AboutPage from './components/pages/AboutPage';
import AdminAnalyticsPage from './components/pages/AdminAnalyticsPage';
import LoginPage from './components/auth/LoginPage';
import SignupPage from './components/auth/SignupPage';
import AuthCallback from './components/auth/AuthCallback';
import ForgotPasswordPage from './components/auth/ForgotPasswordPage';
import ResetPasswordPage from './components/auth/ResetPasswordPage';
import AuthVerification from './components/auth/AuthVerification';

// Common Components
import QuickLinks from './components/common/QuickLinks';
import MessageNotification from './components/common/MessageNotification';
import Footer from './components/layout/Footer';
import { trackPageView, trackSessionStart, trackSessionEnd } from './services/analyticsService';
import { getLastUpdated } from './utils/journalPosts';

const normalizePage = (pageParam, pathname = window.location.pathname) => {
  const path = pathname.replace(/\/$/, '') || '/';
  if (pageParam === 'home' || pageParam === 'journal' || path === '/journal' || path === '/blog') {
    return 'journal';
  }
  if (pageParam) return pageParam;
  if (path === '/') return 'journal';
  return 'journal';
};

const isJournalPage = (page) => page === 'journal' || page === 'home';

const App = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const initialPage = normalizePage(urlParams.get('page'));
  
  const [page, setPage] = useState(initialPage);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [postFilter, setPostFilter] = useState({ tag: null, year: null });
  const [selectedPost, setSelectedPost] = useState(null);
  const [editingPost, setEditingPost] = useState(null);

  // Custom hooks
  const { userId, user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin(userId);

  const adminOnlyPages = ['apps', 'analytics'];

  // Admin-only pages: redirect direct URL access for non-admins
  useEffect(() => {
    if (!adminOnlyPages.includes(page) || adminLoading) return;
    if (!isAdmin) {
      window.history.replaceState({}, '', '/journal');
      setPage('journal');
    }
  }, [page, isAdmin, adminLoading]);
  
  // Handle URL changes for login/signup
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      setPage(normalizePage(params.get('page')));
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Check for password reset token in URL hash (from Supabase email link)
  useEffect(() => {
    const checkForRecoveryToken = () => {
      // Check if URL hash contains recovery token
      const hash = window.location.hash;
      if (hash && hash.includes('type=recovery')) {
        // Supabase will automatically handle the hash and set the session
        // We just need to redirect to reset-password page
        const url = new URL(window.location.href);
        // Keep the hash for Supabase to process, but add page param
        url.searchParams.set('page', 'reset-password');
        window.history.replaceState({}, '', url);
        setPage('reset-password');
      }
    };
    
    // Check immediately on mount
    checkForRecoveryToken();
    
    // Also check on hash change (in case hash is added after page load)
    const handleHashChange = () => {
      checkForRecoveryToken();
    };
    
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Track session start on mount
  useEffect(() => {
    const isAuthPage = ['login', 'signup', 'forgot-password', 'reset-password', 'auth', 'callback', 'auth-verify'].includes(page);
    if (!isAuthPage) {
      trackSessionStart(userId);
    }
  }, []); // Only run once on mount

  // Track page views for analytics
  useEffect(() => {
    // Only track on non-auth pages
    const isAuthPage = ['login', 'signup', 'forgot-password', 'reset-password', 'auth', 'callback', 'auth-verify'].includes(page);
    if (!isAuthPage) {
      const pagePath = isJournalPage(page) ? '/journal' : `/?page=${page}`;
      trackPageView(pagePath, userId);
    }
  }, [page, userId]);

  // Track session end when user leaves
  useEffect(() => {
    // Periodic heartbeat to keep session alive and detect when user is inactive
    const heartbeatInterval = setInterval(() => {
      const sessionId = sessionStorage.getItem('analytics_session_id');
      if (sessionId) {
        // Send heartbeat to update session (this helps detect active sessions)
        fetch('/api/analytics/session-heartbeat', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ session_id: sessionId }),
          keepalive: true
        }).catch(() => {}); // Silently fail
      }
    }, 30000); // Every 30 seconds

    const handleBeforeUnload = () => {
      // Use sendBeacon for reliable tracking when page is closing
      const sessionId = sessionStorage.getItem('analytics_session_id');
      if (sessionId) {
        const endedAt = new Date().toISOString();
        console.log('[SESSION-END] Attempting to track session end:', { sessionId, endedAt });
        
        // Try multiple methods for maximum reliability
        // Method 1: sendBeacon (most reliable for page unload)
        if (navigator.sendBeacon) {
          const data = `session_id=${encodeURIComponent(sessionId)}&ended_at=${encodeURIComponent(endedAt)}`;
          const blob = new Blob([data], { type: 'application/x-www-form-urlencoded' });
          const sent = navigator.sendBeacon('/api/analytics/session-end', blob);
          console.log('[SESSION-END] sendBeacon sent:', sent);
        }
        
        // Method 2: Also try direct client-side update (backup)
        // This uses Supabase directly and might be more reliable
        trackSessionEnd().catch((err) => {
          console.error('[SESSION-END] Client-side tracking error:', err);
        });
        
        // Method 3: Fetch with keepalive (additional backup)
        const fetchData = JSON.stringify({
          session_id: sessionId,
          ended_at: endedAt
        });
        fetch('/api/analytics/session-end', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: fetchData,
          keepalive: true
        }).catch((err) => {
          console.error('[SESSION-END] Fetch error:', err);
        });
      } else {
        console.log('[SESSION-END] No session ID found');
      }
    };

    // Track visibility changes (tab switch, minimize)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched tabs or minimized - could track as pause
        // For now, we'll just log it but not track it
        console.log('[ANALYTICS] Page hidden');
      } else {
        // User came back
        console.log('[ANALYTICS] Page visible');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Also try to track session end on component unmount (if user navigates away)
      trackSessionEnd();
    };
  }, []);

  const { userData, loading: userDataLoading } = useUserData(userId);
  const { latestPost, allPosts, allTags, allYears, loading: postsLoading, refreshPosts } = useBlogPosts();

  const displayedPost = selectedPost || latestPost;

  // Other Articles: every entry except the one currently shown in the main pane
  const filteredPriorPosts = useMemo(() => {
    let posts = allPosts.filter((post) => post.id !== displayedPost?.id);
    const { tag, year } = postFilter;

    if (tag) {
      posts = posts.filter(post => post.tags?.includes(tag));
    }

    if (year) {
      posts = posts.filter(post => new Date(getLastUpdated(post)).getFullYear() === year);
    }

    return posts;
  }, [allPosts, displayedPost?.id, postFilter]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleAuthSuccess = () => {
    console.log('[APP] handleAuthSuccess called');
    // Get return URL from query params or default to home
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return') || '/';
    
    console.log('[APP] Return URL:', returnUrl);
    
    // If return URL is a proxied service (/diet-app or /job-search), go directly there
    // This avoids the bounce through homepage
    if (returnUrl.startsWith('/diet-app') || returnUrl.startsWith('/job-search')) {
      console.log('[APP] Redirecting directly to service:', returnUrl);
      // Go directly to the service - no bounce through homepage
      window.location.href = returnUrl;
      return; // Exit early to prevent any other redirects
    }
    
    // For homepage, clear page param and stay on homepage
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete('page');
    newUrl.searchParams.delete('return');
    window.history.pushState({}, '', '/journal');
    
    setMessage('Successfully logged in!');
    setTimeout(() => setMessage(''), 3000);
    
    setPage('journal');
  };

  const journalSidebar = (
    <aside className="space-y-6">
      {isAdmin && (
        <div className="p-3 bg-green-100 rounded-xl border border-green-300 text-center text-sm font-semibold text-green-800">
          Admin Mode Active
        </div>
      )}

      <BlogSidebar
        priorPosts={filteredPriorPosts}
        allTags={allTags}
        allYears={allYears}
        setFilter={setPostFilter}
        filters={postFilter}
        isAdmin={isAdmin}
        setMessage={setMessage}
        onPostUpdated={refreshPosts}
        onPostClick={(post) => {
          setSelectedPost(post);
          setEditingPost(null);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onEditPost={(post) => {
          setEditingPost(post);
          setSelectedPost(post);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />

      <QuickLinks isAdmin={isAdmin} setMessage={setMessage} />
    </aside>
  );

  const renderContent = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return') || '/';
    
    switch (page) {
      case 'login':
        return <LoginPage onLoginSuccess={handleAuthSuccess} returnUrl={returnUrl} />;
      case 'signup':
        return <SignupPage onSignupSuccess={handleAuthSuccess} returnUrl={returnUrl} />;
      case 'forgot-password':
        return <ForgotPasswordPage onBack={() => {
          const url = new URL(window.location.href);
          url.searchParams.set('page', 'login');
          if (returnUrl) url.searchParams.set('return', returnUrl);
          window.history.pushState({}, '', url);
          setPage('login');
        }} />;
      case 'reset-password':
        return <ResetPasswordPage onSuccess={handleAuthSuccess} />;
      case 'auth':
      case 'callback':
        return <AuthCallback onAuthSuccess={handleAuthSuccess} />;
      case 'auth-verify':
        return <AuthVerification />;
      case 'apps':
        if (adminLoading) {
          return (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          );
        }
        if (!isAdmin) {
          return null;
        }
        return <AppsPage userData={userData} userId={userId} isAdmin={isAdmin} setMessage={setMessage} />;
      case 'about':
        return <AboutPage isAdmin={isAdmin} setMessage={setMessage} />;
      case 'analytics':
        if (adminLoading) {
          return (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          );
        }
        if (!isAdmin) return null;
        return <AdminAnalyticsPage isAdmin={isAdmin} setMessage={setMessage} />;
      case 'journal':
      case 'home':
      default:
        // When editing a prior post, show edit form in full main pane
        if (editingPost) {
          return (
            <EditPostForm
              post={editingPost}
              isAdmin={isAdmin}
              setMessage={setMessage}
              fullPane
              onPostUpdated={() => {
                setEditingPost(null);
                refreshPosts();
              }}
              onCancel={() => setEditingPost(null)}
            />
          );
        }
        // Show selected post if one is selected, otherwise show latest post
        const postToDisplay = selectedPost || latestPost;
        return (
          <LatestPostDisplay
            key={postToDisplay?.id}
            post={postToDisplay}
            isLatestPost={!selectedPost}
            onShowLatestPost={() => setSelectedPost(null)}
            userId={userId}
            isAdmin={isAdmin}
            setMessage={setMessage}
            sidebar={journalSidebar}
            onPostUpdated={() => {
              refreshPosts();
              setSelectedPost(null); // Reset selection after update
            }}
          />
        );
    }
  };

  // Don't show navbar on auth pages
  const showNavBar = !['login', 'signup', 'forgot-password', 'reset-password', 'auth', 'callback', 'auth-verify'].includes(page);
  
  // For auth pages, don't wait for auth to load - show them immediately
  const isAuthPage = ['login', 'signup', 'forgot-password', 'reset-password', 'auth', 'callback', 'auth-verify'].includes(page);
  
  // Only show loading spinner for non-auth pages while auth is loading
  // But add a delay to prevent flash on fast auth checks
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  useEffect(() => {
    if (authLoading && !isAuthPage) {
      const timer = setTimeout(() => setShowLoadingSpinner(true), 500);
      return () => clearTimeout(timer);
    } else {
      setShowLoadingSpinner(false);
    }
  }, [authLoading, isAuthPage]);

  if (showLoadingSpinner) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper font-sans antialiased text-stone-800 flex flex-col">
      {showNavBar && (
        <NavBar 
          currentPage={page} 
          setPage={setPage} 
          showMobileMenu={isMobileMenuOpen} 
          setShowMobileMenu={setIsMobileMenuOpen} 
          user={user}
          isAdmin={isAdmin}
          onNavigateHome={() => {
            window.history.pushState({}, '', '/journal');
            setPage('journal');
            setSelectedPost(null);
            setEditingPost(null);
            setIsMobileMenuOpen(false);
          }}
        />
      )}

      <main className={showNavBar ? "max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10 flex-1" : "flex-1"}>
        {isJournalPage(page) ? (
          editingPost ? (
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              <div className="lg:col-span-2 space-y-8">
                {renderContent()}
              </div>
              {journalSidebar}
            </div>
          ) : (
            renderContent()
          )
        ) : (
          /* Apps and About pages handle their own layout */
          renderContent()
        )}
      </main>

      {/* Footer - show on all pages except auth pages */}
      {showNavBar && <Footer />}

      {/* Message/Notification Box */}
      <MessageNotification message={message} />
    </div>
  );
};

export default App;
